import React, { useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, PartyPopper } from "lucide-react";
import { Button } from "./ui/button";
import StepIndicator from "./StepIndicator";
import FormField from "./FormField";
import { formSteps } from "../mock";

function validateStep(fields, data) {
  const errors = {};
  fields.forEach((f) => {
    if (!f.required) return;
    const val = data[f.name];
    if (f.type === "checkbox") {
      if (!val) errors[f.name] = "Você precisa aceitar para continuar.";
    } else if (f.type === "file") {
      if (!val || !val.url) errors[f.name] = "Envie o documento solicitado.";
    } else if (!val || String(val).trim() === "") {
      errors[f.name] = "O campo é obrigatório.";
    } else if (f.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      errors[f.name] = "Informe um e-mail válido.";
    } else if (f.mask === "date" && !/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
      errors[f.name] = "Use o formato DD/MM/AAAA.";
    }
  });
  return errors;
}

export default function RegistrationForm({ onSubmit }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const currentStep = formSteps[step];
  const isLast = step === formSteps.length - 1;

  const setField = (name, value) => {
    setData((d) => ({ ...d, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const handleNext = async () => {
    const stepErrors = validateStep(currentStep.fields, data);
    if (Object.keys(stepErrors).length) {
      setErrors(stepErrors);
      return;
    }
    if (!isLast) {
      setStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(data);
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (done) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-16 text-center fade-in">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#FCFC30]">
          <PartyPopper className="h-10 w-10 text-[#0A1B6B]" />
        </div>
        <h2 className="font-heading text-2xl font-extrabold text-[#0A1B6B]">Cadastro enviado!</h2>
        <p className="max-w-md text-slate-500">
          Recebemos suas informações com sucesso. Nossa equipe de Representantes Autorizados
          BB Consórcios entrará em contato em breve pelo WhatsApp informado.
        </p>
        <Button
          onClick={() => { setDone(false); setStep(0); setData({}); }}
          className="mt-2 bg-[#465EFF] text-white hover:bg-[#3448d6]"
        >
          Fazer novo cadastro
        </Button>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <div className="mb-6 flex items-center justify-between">
        <span className="font-heading text-sm font-bold text-[#465EFF]">
          {step + 1}<span className="text-slate-300">/{formSteps.length}</span>
        </span>
      </div>

      <StepIndicator steps={formSteps} current={step} />

      <div className="mt-8">
        <h2 className="font-heading text-xl font-extrabold text-[#0A1B6B]">{currentStep.title}</h2>
        <p className="mt-1 text-sm text-slate-500">{currentStep.subtitle}</p>
      </div>

      <div key={step} className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 fade-in">
        {currentStep.fields.map((field) => (
          <FormField
            key={field.name}
            field={field}
            value={data[field.name]}
            error={errors[field.name]}
            onChange={(v) => setField(field.name, v)}
          />
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between gap-4 border-t border-slate-100 pt-6">
        <Button
          type="button"
          variant="ghost"
          onClick={handleBack}
          disabled={step === 0}
          className="text-slate-500 hover:text-[#0A1B6B] disabled:opacity-0"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
        </Button>
        <Button
          type="button"
          onClick={handleNext}
          disabled={submitting}
          className="bg-[#465EFF] px-6 text-white shadow-lg shadow-[#465EFF]/25 hover:bg-[#3448d6]"
        >
          {submitting ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enviando...</>
          ) : isLast ? (
            <><CheckCircle2 className="mr-2 h-4 w-4" /> Concluir cadastro</>
          ) : (
            <>Próximo <ArrowRight className="ml-2 h-4 w-4" /></>
          )}
        </Button>
      </div>
    </div>
  );
}
